/**
 * 图集阿里云 HLS 后台预缓存：仅拉 HLS preview + CDN 分片，不调用 getDownloadUrl（不占整文件下载次数）。
 */
import {
  fetchVideoAliyunPreview,
  isAliyunAlbumVideo,
  pickHighestStreamUrl,
  prepareHlsStreams
} from '@/utils/aliyunHlsPlayback.js';
import {
  buildManifestKey,
  buildSegmentKey,
  getSegment,
  isVideoFullyCached,
  normalizeSegmentUrl,
  putManifest,
  putPlaylist,
  putSegment,
  rewritePlaylistWithAbsoluteUrls
} from '@/services/videoSegmentCache.js';

const LOG_PREFIX = '[albumHlsBgCache]';
const DEFAULT_CONCURRENCY = 2;

/** @type {{ key: string, cancel: boolean } | null} */
let activeJob = null;

function parsePlaylistSegmentUrls(playlistText, playlistUrl) {
  if (!playlistText || typeof playlistText !== 'string') return [];
  const urls = [];
  for (const raw of playlistText.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    try {
      urls.push(new URL(line, playlistUrl).href);
    } catch (_) { /* skip */ }
  }
  return urls;
}

async function fetchText(url) {
  const resp = await fetch(url, {
    referrerPolicy: 'no-referrer',
    credentials: 'omit',
    mode: 'cors'
  });
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
  return resp.text();
}

async function fetchSegmentBytes(url) {
  const resp = await fetch(url, {
    referrerPolicy: 'no-referrer',
    credentials: 'omit',
    mode: 'cors'
  });
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
  return resp.arrayBuffer();
}

/**
 * 将单个图集视频的最高档 HLS 全部分片写入 IndexedDB。
 */
export async function prefetchAlbumVideoHighestHls(videoId, musicId) {
  if (videoId == null) return false;
  if (await isVideoFullyCached(videoId)) {
    return true;
  }

  const preview = await fetchVideoAliyunPreview(videoId, musicId);
  if (preview?.playMethod !== 'hls' || !preview.hlsStreams?.length) {
    return false;
  }

  const streams = prepareHlsStreams(preview.hlsStreams);
  const levelUrl = pickHighestStreamUrl(streams);
  if (!levelUrl) return false;

  const levelKey = normalizeSegmentUrl(levelUrl);
  if (!levelKey) return false;

  const playlistText = await fetchText(levelUrl);
  await putPlaylist(videoId, levelKey, rewritePlaylistWithAbsoluteUrls(playlistText, levelKey));

  const segUrls = parsePlaylistSegmentUrls(playlistText, levelUrl);
  if (!segUrls.length) return false;

  const segmentKeys = [];
  for (const segUrl of segUrls) {
    const norm = normalizeSegmentUrl(segUrl);
    if (!norm) continue;
    const segKey = buildSegmentKey(videoId, norm);
    segmentKeys.push(segKey);
    const cached = await getSegment(segKey);
    if (cached?.byteLength > 0) continue;
    const bytes = await fetchSegmentBytes(segUrl);
    if (bytes?.byteLength > 0) {
      await putSegment(segKey, bytes);
    }
  }

  await putManifest(buildManifestKey(videoId, levelKey), {
    videoId,
    levelKey,
    segmentKeys,
    total: segmentKeys.length,
    updatedAt: Date.now()
  });

  return !!(await isVideoFullyCached(videoId));
}

async function runWithConcurrency(items, worker, concurrency) {
  let index = 0;
  const runners = Array.from({ length: concurrency }, async () => {
    while (index < items.length) {
      if (activeJob?.cancel) return;
      const current = items[index];
      index += 1;
      try {
        await worker(current);
      } catch (error) {
        console.warn(`${LOG_PREFIX} 预缓存失败 videoId=${current.videoId}:`, error?.message || error);
      }
    }
  });
  await Promise.all(runners);
}

/**
 * 图集加载完成后后台预缓存所有阿里云短视频（最高档 HLS 分片）。
 * @param {Array} mediaItems
 * @param {number|string} musicId
 * @param {{ concurrency?: number }} options
 */
export function startAlbumHlsBackgroundCache(mediaItems, musicId, options = {}) {
  if (!Array.isArray(mediaItems) || mediaItems.length === 0 || musicId == null) {
    return;
  }

  const targets = mediaItems.filter((item) => isAliyunAlbumVideo(item));
  if (!targets.length) return;

  const jobKey = `${musicId}:${targets.map((t) => t.videoId).join(',')}`;
  if (activeJob?.key === jobKey) return;

  if (activeJob) {
    activeJob.cancel = true;
  }
  activeJob = { key: jobKey, cancel: false };

  const concurrency = options.concurrency ?? DEFAULT_CONCURRENCY;
  console.log(`${LOG_PREFIX} 开始后台预缓存 ${targets.length} 个图集视频（最高档 HLS 分片，并发 ${concurrency}）`);

  (async () => {
    const job = activeJob;
    await runWithConcurrency(
      targets,
      async (item) => {
        if (job.cancel) return;
        const ok = await prefetchAlbumVideoHighestHls(item.videoId, musicId);
        if (ok) {
          console.log(`${LOG_PREFIX} 完成 videoId=${item.videoId}`);
        }
      },
      concurrency
    );
    if (!job.cancel) {
      console.log(`${LOG_PREFIX} 后台预缓存任务结束`);
    }
    if (activeJob === job) {
      activeJob = null;
    }
  })();
}

export function cancelAlbumHlsBackgroundCache() {
  if (activeJob) {
    activeJob.cancel = true;
    activeJob = null;
  }
}
